import React, { FormEvent, useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

interface RegistrationCheckoutFormProps {
  total?: number;
  preProps?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  redirectRoute?: string;
  onPaymentError?: (message: string) => void;
}

export default function RegistrationCheckoutForm({
  total,
  preProps,
  redirectRoute = "registration",
  onPaymentError,
}: RegistrationCheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();

  const [email, setEmail] = useState(preProps?.email ? preProps.email : "");
  const [name, setName] = useState(
    preProps?.firstName && preProps?.lastName
      ? `${preProps.firstName} ${preProps.lastName}`
      : ""
  );
  const [phone, setPhone] = useState(preProps?.phone ? preProps.phone : "");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:1313"
            : `${window.location.origin}`
        }/${redirectRoute}`,
        receipt_email: email,
        payment_method_data: {
          billing_details: {
            name,
            phone,
            address: {
              line1: address,
              city,
            },
          },
        },
      },
    });

    if (error.type === "card_error" || error.type === "validation_error") {
      onPaymentError?.(error.message || "Your card was declined. Please try again.");
    } else {
      onPaymentError?.("An unexpected payment error occurred. Please try again.");
    }

    setIsLoading(false);
  };

  const fieldLabelStyle = {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
    color: "#30313d",
    fontSize: "0.93rem",
    fontWeight: "400",
    marginBottom: "0.25rem",
  } as const;

  const fieldInputStyle = {
    border: "1px solid rgba(50, 50, 93, 0.1)",
    maxHeight: 47,
    width: "100%",
    boxShadow: "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
  } as const;

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      {stripe && elements && (
        <>
          <p className="mb-6 text-sm text-gray-600">
            Enter your payment details below. You&apos;ll receive a confirmation email after
            checkout.
          </p>
          <label htmlFor="email" style={fieldLabelStyle}>
            Email
          </label>
          <input
            id="email"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-md mb-4 p-3 bg-white"
            style={fieldInputStyle}
          />

          <label htmlFor="name" style={fieldLabelStyle}>
            Full name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-md mb-4 p-3 bg-white"
            style={fieldInputStyle}
          />
          <label htmlFor="address" style={fieldLabelStyle}>
            Address
          </label>
          <input
            id="address"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="rounded-md mb-4 p-3 bg-white"
            style={fieldInputStyle}
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="city" style={fieldLabelStyle}>
                City
              </label>
              <input
                id="city"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="rounded-md mb-4 p-3 bg-white"
                style={fieldInputStyle}
              />
            </div>
            <div>
              <label htmlFor="phone" style={fieldLabelStyle}>
                Phone number
              </label>
              <input
                id="phone"
                type="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="rounded-md mb-4 p-3 bg-white"
                style={fieldInputStyle}
              />
            </div>
          </div>
          <PaymentElement id="payment-element" />
          <div className="flex flex-col w-full justify-center mt-4">
            <button
              disabled={isLoading || !stripe || !elements}
              id="submit"
              className="btn"
            >
              <span id="button-text">
                {isLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <div className="spinner" id="spinner"></div>
                    Processing payment...
                  </span>
                ) : (
                  `Pay${total ? ` $${(total / 100).toFixed(2)}` : ""}`
                )}
              </span>
            </button>
          </div>
        </>
      )}
    </form>
  );
}
